import {createOpenAI, type openai} from '@ai-sdk/openai';
import type {ModelMessage} from 'ai';
import {generateText, streamText} from 'ai';
import {parseCommandFromResponse} from '../exec/parser.js';
import type {
  LooseToStrict,
  ModelInfo,
  OpenAISettings,
  RawiCredentials,
  StreamingResponse,
} from '../shared/index.js';
import type {
  ChatCredentials,
  ChatProvider,
  ChatStreamOptions,
  ExecGenerationOptions,
  ExecGenerationResult,
} from './types.js';

type LooseOpenAIModelId = Parameters<typeof openai>[0];
export type OpenAIModelId = LooseToStrict<LooseOpenAIModelId>;

const openAIModelIds = [
  'o1',
  'o1-2024-12-17',
  'o3-mini',
  'o3-mini-2025-01-31',
  'o3',
  'o3-2025-04-16',
  'gpt-4.1',
  'gpt-4.1-2025-04-14',
  'gpt-4.1-mini',
  'gpt-4.1-mini-2025-04-14',
  'gpt-4.1-nano',
  'gpt-4.1-nano-2025-04-14',
  'gpt-4o',
  'gpt-4o-2024-05-13',
  'gpt-4o-2024-08-06',
  'gpt-4o-2024-11-20',
  'gpt-4o-mini',
  'gpt-4o-mini-2024-07-18',
  'gpt-4-turbo',
  'gpt-4-turbo-2024-04-09',
  'gpt-4',
  'gpt-4-0613',
  'gpt-4.5-preview',
  'gpt-4.5-preview-2025-02-27',
  'gpt-3.5-turbo-0125',
  'gpt-3.5-turbo',
  'gpt-3.5-turbo-1106',
  'chatgpt-4o-latest',
  'gpt-5',
  'gpt-5-2025-08-07',
  'gpt-5-mini',
  'gpt-5-mini-2025-08-07',
  'gpt-5-nano',
  'gpt-5-nano-2025-08-07',
  'gpt-5-chat-latest',
] as const;

export const openaiModels: ModelInfo[] = openAIModelIds.map((name) => ({
  name,
  displayName: name,
}));

export const openaiProvider = {
  name: 'openai' as const,
  displayName: '🔵 OpenAI (GPT)',
  models: openaiModels,
};

export const streamWithOpenAI = async (
  credentials: RawiCredentials,
  prompt: string,
): Promise<StreamingResponse> => {
  try {
    const settings = credentials.providerSettings as OpenAISettings | undefined;

    const apiKey = settings?.apiKey || credentials.apiKey;

    if (!credentials.apiKey) {
      throw new Error('API key is required for OpenAI');
    }

    const baseURL = settings?.baseURL || 'https://api.openai.com/v1';

    const openaiProvider = createOpenAI({
      apiKey: apiKey,
      baseURL: baseURL,
    });

    const result = streamText({
      model: openaiProvider(credentials.model),
      prompt,
      temperature: credentials.temperature || 0.7,
      maxOutputTokens: credentials.maxTokens || 2048,
    });

    return {
      textStream: result.textStream,
      fullResponse: result.text,
    };
  } catch (error) {
    throw new Error(
      `Error calling OpenAI streaming API: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }
};

export const openaiChatProvider: ChatProvider = {
  name: 'openai',
  displayName: '🔵 OpenAI (GPT)',

  async streamChat(
    credentials: ChatCredentials,
    messages: ModelMessage[],
    options: ChatStreamOptions = {},
  ): Promise<AsyncIterable<string>> {
    const settings = credentials.providerSettings || {};
    const apiKey = settings.apiKey || credentials.apiKey;

    if (!apiKey) {
      throw new Error('API key is required for OpenAI');
    }

    const openaiProvider = createOpenAI({
      apiKey: apiKey,
      baseURL: settings.baseURL || 'https://api.openai.com/v1',
    });

    const result = streamText({
      model: openaiProvider(credentials.model),
      messages,
      temperature: credentials.temperature || options.temperature || 0.7,
      maxOutputTokens: credentials.maxTokens || options.maxTokens || 2048,
    });

    return result.textStream;
  },
};

export const generateWithOpenAI = async (
  options: ExecGenerationOptions,
): Promise<ExecGenerationResult> => {
  const startTime = Date.now();

  try {
    const openAISettings = options.credentials
      .providerSettings as OpenAISettings;

    const openAIProvider = createOpenAI({
      apiKey: options.credentials.apiKey,
      baseURL: openAISettings?.baseURL,
    });

    const result = await generateText({
      model: openAIProvider(options.credentials.model),
      system: options.systemPrompt,
      prompt: options.userPrompt,
    });

    const generationTime = Date.now() - startTime;

    const command = parseCommandFromResponse(result.text);

    return {
      command,
      generationTime,
    };
  } catch (error) {
    throw new Error(`OpenAI exec generation failed: ${error}`);
  }
};
